import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Tooltip } from '@mui/material';
import {
  CameraIcon,
  MicrophoneIcon,
  ShareScreenIcon,
  CallEndIcon,
  FullScreenIcon,
  FullScreenExitIcon,
  FlipCameraIcon,
  ChatIcon,
  CameraDisabledIcon,
  ShareScreenDisableIcon,
  MicrophoneDisabledIcon,
} from 'components/elements/Icons';
import { useDeviceDetect, useVideoContext, useLocalAudioToggle, useLocalVideoToggle, useFlipCameraToggle, useScreenShareParticipant } from 'hooks';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function VideoControls({ isVisible, onToggleFullScreenMode, onToggleMobileChat, isFullScreenMode }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { isDesktop } = useDeviceDetect();
  const { isSharingScreen: isLocalSharingScreen, toggleScreenShare, disconnect } = useVideoContext();
  const [hasAudioDeviceSelected, isAudioEnabled, toggleAudio] = useLocalAudioToggle();
  const [hasVideoDeviceSelected, isVideoEnabled, toggleVideo] = useLocalVideoToggle();
  const { flipCameraDisabled, toggleFacingMode, flipCameraSupported, isEnvironmentMode } = useFlipCameraToggle();
  const { isSharingScreen } = useScreenShareParticipant();

  return (
    <div className={cn(classes.videoControls, { [classes.videoControlsActive]: isVisible })}>
      <Tooltip title={t('full_screen_mode')}>
        <button type="button" className={classes.fullScreenButton} onClick={onToggleFullScreenMode}>
          {isFullScreenMode ? <FullScreenExitIcon /> : <FullScreenIcon />}
        </button>
      </Tooltip>
      <div className={classes.controlButtons}>
        <Tooltip title={t(isAudioEnabled ? 'mute' : 'unmute')} placement="top">
          <button
            className={cn(classes.controlButton, { [classes.inactiveControlButton]: !isAudioEnabled })}
            type="button"
            onClick={toggleAudio}
            disabled={!hasAudioDeviceSelected}
          >
            {isAudioEnabled ? <MicrophoneIcon classes={classes.icon} /> : <MicrophoneDisabledIcon classes={classes.icon} />}
          </button>
        </Tooltip>
        <Tooltip title={t(isVideoEnabled ? 'camera_off' : 'camera_on')} placement="top">
          <button
            className={cn(classes.controlButton, { [classes.inactiveControlButton]: !isVideoEnabled })}
            type="button"
            onClick={toggleVideo}
            disabled={!hasVideoDeviceSelected}
          >
            {isVideoEnabled ? <CameraIcon classes={classes.icon} /> : <CameraDisabledIcon classes={classes.icon} />}
          </button>
        </Tooltip>
        {isDesktop ? (
          <Tooltip title={t(isLocalSharingScreen ? 'end_screen_share' : 'share_screen')} placement="top">
            <button
              className={cn(classes.controlButton, { [classes.inactiveControlButton]: !(isSharingScreen || isLocalSharingScreen) })}
              type="button"
              onClick={toggleScreenShare}
              disabled={isSharingScreen && !isLocalSharingScreen}
            >
              {!isLocalSharingScreen ? <ShareScreenIcon classes={classes.icon} /> : <ShareScreenDisableIcon classes={classes.icon} />}
            </button>
          </Tooltip>
        ) : (
          <>
            {flipCameraSupported && (
              <Tooltip title={t('change_camera')} placement="top">
                <button style={{ opacity: isEnvironmentMode ? 0.5 : 1 }} className={classes.controlButton} type="button" onClick={toggleFacingMode} disabled={flipCameraDisabled}>
                  <FlipCameraIcon classes={classes.icon} />
                </button>
              </Tooltip>
            )}
            <Tooltip title={t('chat')} placement="top">
              <button className={classes.controlButton} type="button" onClick={onToggleMobileChat}>
                <ChatIcon classes={classes.icon} />
              </button>
            </Tooltip>
          </>
        )}
        <Tooltip title={t('end_call')} placement="top">
          <button className={classes.endCallControlButton} type="button" onClick={disconnect}>
            <CallEndIcon classes={classes.icon} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

VideoControls.propTypes = {
  onToggleMobileChat: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onToggleFullScreenMode: PropTypes.func.isRequired,
  isFullScreenMode: PropTypes.bool.isRequired,
};

export default VideoControls;
